import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { FileText, Plus, Trash2, Download, Edit, Save, X, Send, Eye, CheckCircle, XCircle, ArrowRight, Link } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

// Estado badges con colores
const ESTADO_CONFIG = {
  borrador: { label: 'Borrador', color: 'bg-slate-600 text-slate-200', icon: '📝' },
  enviado: { label: 'Enviado', color: 'bg-blue-600 text-blue-100', icon: '📤' },
  visto: { label: 'Visto', color: 'bg-purple-600 text-purple-100', icon: '👁️' },
  aceptado: { label: 'Aceptado', color: 'bg-green-600 text-green-100', icon: '✅' },
  rechazado: { label: 'Rechazado', color: 'bg-red-600 text-red-100', icon: '❌' },
  pendiente: { label: 'Pendiente', color: 'bg-yellow-600 text-yellow-100', icon: '⏳' },
};

export const GestionPresupuestos = ({ playlist, API, onEventCreated }) => {
  const [presupuestos, setPresupuestos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [precios, setPrecios] = useState([]);
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [convertingPresupuesto, setConvertingPresupuesto] = useState(null);
  const [isConverting, setIsConverting] = useState(false);
  const [formData, setFormData] = useState({
    cliente_nombre: playlist?.nombre_novios || '',
    cliente_email: playlist?.email_novios || '',
    cliente_telefono: playlist?.telefono_novios || '',
    fecha_evento: playlist?.fecha_evento || '',
    servicios: [],
    notas: '',
    descuento_porcentaje: 0,
    retencion_irpf: 0,
    iva: 10,
    validez_dias: 15
  });
  const [currentServicio, setCurrentServicio] = useState({
    nombre: '',
    descripcion: '',
    precio: 0,
    cantidad: 1
  });

  useEffect(() => {
    loadPresupuestos();
    loadPrecios();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- deps: loadPrecios and loadPresupuestos
  }, []);

  const loadPrecios = async () => {
    try {
      // Pass venue parameter to filter services based on event location
      const params = {};
      if (playlist?.lugar_evento) {
        params.venue = playlist.lugar_evento;
      }
      const response = await axios.get(`${API}/precios`, { params });
      setPrecios(response.data.precios || []);
    } catch (error) {
      console.error('Error loading precios:', error);
    }
  };

  // Filter precios to show only active ones and respect venue exclusivity
  const preciosFiltrados = precios.filter(p => {
    if (!p.activo) return false;
    // If service has no venue_exclusivo, always show it
    if (!p.venue_exclusivo) return true;
    // If service has venue_exclusivo, show only if it matches event's lugar_evento
    return playlist?.lugar_evento && 
           p.venue_exclusivo.toLowerCase().includes(playlist.lugar_evento.toLowerCase());
  });

  const autoFillServicios = () => {
    if (!playlist) return;
    
    const serviciosContratados = playlist.servicios_contratados || [];
    const lugarEvento = playlist.lugar_evento || '';
    const duracionBaile = playlist.duracion_baile || ''; // Duración del baile/DJ (ej: "3", "4h", "5 horas")
    
    // Filtrar solo los servicios que están marcados como contratados
    const serviciosActivos = serviciosContratados.filter(s => s.contratado);
    
    if (serviciosActivos.length === 0) {
      alert('Este evento no tiene servicios contratados');
      return;
    }
    
    // Extraer número de horas de duracion_baile (ej: "3" → 3, "4h" → 4, "5 horas" → 5)
    const horasMatch = duracionBaile.match(/(\d+)/);
    const horasBaile = horasMatch ? parseInt(horasMatch[1]) : null;
    
    const serviciosAutoFill = [];
    
    serviciosActivos.forEach(servicioObj => {
      const servicioNombre = servicioObj.servicio; // Nombre del servicio (ej: "DJ", "Fotomatón", "DJ 2H evento boda (Delicatto)")
      const servicioNotas = servicioObj.notas || ''; // Notas adicionales del servicio
      const servicioCantidad = servicioObj.cantidad || 1; // Cantidad contratada
      const servicioLower = servicioNombre.toLowerCase();
      
      // ============================================
      // PRIORIDAD 1: Usar precio del servicio contratado si existe
      // Esto es crucial para servicios específicos de venue (ej: Delicatto)
      // ============================================
      // Usar precio_personalizado si está definido (incluso si es 0)
      // Si no, usar precio_final, y si no, precio_base
      const tienePrecioPersonalizado = servicioObj.precio_personalizado !== null && servicioObj.precio_personalizado !== undefined;
      const tienePrecioFinal = servicioObj.precio_final !== null && servicioObj.precio_final !== undefined;
      const tienePrecioBase = servicioObj.precio_base !== null && servicioObj.precio_base !== undefined;
      
      const precioDelServicioContratado = tienePrecioPersonalizado 
        ? servicioObj.precio_personalizado 
        : (tienePrecioFinal ? servicioObj.precio_final : (tienePrecioBase ? servicioObj.precio_base : null));
      
      // Si el servicio contratado tiene un precio válido (incluso 0), usarlo directamente
      // Solo caer a PRIORIDAD 2 si no tiene ningún precio definido (todos son null/undefined)
      if (precioDelServicioContratado !== null) {
        // Usar el nombre y precio tal como están en el servicio contratado
        serviciosAutoFill.push({
          nombre: servicioNombre,
          descripcion: servicioNotas,
          precio: precioDelServicioContratado,
          cantidad: servicioCantidad,
          subtotal: precioDelServicioContratado * servicioCantidad
        });
        return; // Salir del forEach para este servicio, no buscar en precios
      }
      
      // ============================================
      // PRIORIDAD 2: Buscar en tabla de precios (solo si no tiene precio definido)
      // ============================================
      const esDJ = servicioLower.includes('dj') && !servicioLower.includes('delicatto'); // DJ genérico, no Delicatto
      
      let precioServicio = null;
      let nombreFinal = servicioNombre;
      let descripcionFinal = servicioNotas;
      let cantidadFinal = servicioCantidad;
      
      if (esDJ && horasBaile) {
        // Para DJ genérico: buscar específicamente "DJ X horas" en la sección de precios filtrados por venue
        precioServicio = preciosFiltrados.find(p => 
          p.nombre_servicio.toLowerCase().includes('dj') &&
          p.nombre_servicio.toLowerCase().includes(horasBaile.toString())
        );
        
        if (precioServicio) {
          nombreFinal = precioServicio.nombre_servicio;
          descripcionFinal = servicioNotas || precioServicio.descripcion || '';
        } else {
          // Si no encuentra el precio exacto, usar nombre genérico
          nombreFinal = `DJ ${horasBaile} ${horasBaile === 1 ? 'Hora' : 'Horas'}`;
        }
      } else {
        // Para otros servicios: buscar en precios filtrados por venue
        precioServicio = preciosFiltrados.find(p => 
          p.nombre_servicio.toLowerCase().includes(servicioLower) ||
          servicioLower.includes(p.nombre_servicio.toLowerCase())
        );
        
        if (precioServicio) {
          nombreFinal = precioServicio.nombre_servicio;
          descripcionFinal = servicioNotas 
            ? (precioServicio.descripcion ? `${servicioNotas} - ${precioServicio.descripcion}` : servicioNotas)
            : (precioServicio.descripcion || '');
        }
      }
      
      // Obtener el precio de la tabla de precios
      let precioFinal = 0;
      if (precioServicio) {
        precioFinal = precioServicio.precio_base;
        
        // Verificar si hay precio especial para el lugar
        const precioEspecial = precioServicio.precios_especiales?.find(pe => 
          lugarEvento.toLowerCase().includes(pe.lugar.toLowerCase())
        );
        
        if (precioEspecial) {
          precioFinal = precioEspecial.precio;
        }
      }
      
      serviciosAutoFill.push({
        nombre: nombreFinal,
        descripcion: descripcionFinal,
        precio: precioFinal,
        cantidad: cantidadFinal,
        subtotal: precioFinal * cantidadFinal
      });
    });
    
    setFormData({
      ...formData,
      servicios: serviciosAutoFill
    });
    
    const mensaje = `Auto-relleno completado: ${serviciosAutoFill.length} servicios desde el evento`;
    
    toast.success(mensaje);
  };

  // Función para actualizar un servicio existente en la lista
  const handleUpdateServicio = (index, field, value) => {
    const updatedServicios = [...formData.servicios];
    updatedServicios[index] = {
      ...updatedServicios[index],
      [field]: value,
      subtotal: field === 'precio' || field === 'cantidad' 
        ? (field === 'precio' ? value : updatedServicios[index].precio) * 
          (field === 'cantidad' ? value : updatedServicios[index].cantidad)
        : updatedServicios[index].subtotal
    };
    setFormData({ ...formData, servicios: updatedServicios });
  };

  const loadPresupuestos = async () => {
    try {
      const response = await fetch(
        `${API}/playlists/${playlist.id}/presupuestos`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (!response.ok) throw new Error('Error al cargar presupuestos');
      const data = await response.json();
      setPresupuestos(data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddServicio = () => {
    if (!currentServicio.nombre || currentServicio.precio < 0) {
      alert('Por favor completa el nombre del servicio');
      return;
    }

    const subtotal = currentServicio.precio * currentServicio.cantidad;
    setFormData({
      ...formData,
      servicios: [...formData.servicios, { ...currentServicio, subtotal }]
    });

    setCurrentServicio({
      nombre: '',
      descripcion: '',
      precio: 0,
      cantidad: 1
    });
  };

  const handleRemoveServicio = (index) => {
    setFormData({
      ...formData,
      servicios: formData.servicios.filter((_, i) => i !== index)
    });
  };

  const calculateTotals = () => {
    const subtotal = formData.servicios.reduce((sum, s) => sum + (s.precio * s.cantidad), 0);
    const total_descuento = subtotal * (formData.descuento_porcentaje / 100);
    const base_imponible = subtotal - total_descuento;
    const total_retencion = base_imponible * (formData.retencion_irpf / 100);
    const total_iva = base_imponible * (formData.iva / 100);
    const total = base_imponible - total_retencion + total_iva;
    return { subtotal, total_descuento, base_imponible, total_retencion, total_iva, total };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.servicios.length === 0) {
      alert('Añade al menos un servicio');
      return;
    }

    try {
      const url = editingId 
        ? `${API}/presupuestos/${editingId}`
        : `${API}/presupuestos`;

      const method = editingId ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          ...formData,
          playlist_id: playlist.id
        })
      });

      if (!response.ok) throw new Error('Error al guardar presupuesto');

      await loadPresupuestos();
      resetForm();
      alert(editingId ? 'Presupuesto actualizado' : 'Presupuesto creado');
    } catch (error) {
      console.error('Error:', error);
      alert('Error al guardar el presupuesto');
    }
  };

  const handleEdit = (presupuesto) => {
    setFormData({
      cliente_nombre: presupuesto.cliente_nombre,
      cliente_email: presupuesto.cliente_email,
      cliente_telefono: presupuesto.cliente_telefono,
      fecha_evento: presupuesto.fecha_evento,
      servicios: presupuesto.servicios,
      notas: presupuesto.notas,
      descuento_porcentaje: presupuesto.descuento_porcentaje || 0,
      retencion_irpf: presupuesto.retencion_irpf || 0,
      iva: presupuesto.iva,
      validez_dias: presupuesto.validez_dias
    });
    setEditingId(presupuesto.id);
    setShowForm(true);
  };

  const handleDelete = async (presupuestoId) => {
    if (!window.confirm('¿Eliminar este presupuesto?')) return;

    try {
      console.log('Deleting presupuesto:', presupuestoId, 'API:', API);
      const response = await fetch(
        `${API}/presupuestos/${presupuestoId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      console.log('Delete response status:', response.status);
      if (!response.ok) {
        const errorData = await response.text();
        console.error('Delete error:', errorData);
        throw new Error('Error al eliminar');
      }
      
      await loadPresupuestos();
      alert('Presupuesto eliminado exitosamente');
    } catch (error) {
      console.error('Error deleting:', error);
      alert('Error al eliminar el presupuesto: ' + error.message);
    }
  };

  const handleDownloadPDF = async (presupuestoId, clienteNombre) => {
    try {
      console.log('Downloading PDF:', presupuestoId, 'API:', API);
      const response = await fetch(
        `${API}/presupuestos/${presupuestoId}/export-pdf`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      console.log('PDF response status:', response.status);
      if (!response.ok) {
        const errorData = await response.text();
        console.error('PDF error:', errorData);
        throw new Error('Error al generar PDF');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `presupuesto_${clienteNombre.replace(/\s+/g, '_')}_${presupuestoId.substring(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Error al descargar el PDF: ' + error.message);
    }
  };

  const resetForm = () => {
    setFormData({
      cliente_nombre: playlist?.nombre_novios || '',
      cliente_email: playlist?.email_novios || '',
      cliente_telefono: playlist?.telefono_novios || '',
      fecha_evento: playlist?.fecha_evento || '',
      servicios: [],
      notas: '',
      descuento_porcentaje: 0,
      retencion_irpf: 0,
      iva: 10,
      validez_dias: 15
    });
    setCurrentServicio({
      nombre: '',
      descripcion: '',
      precio: 0,
      cantidad: 1
    });
    setEditingId(null);
    setShowForm(false);
  };

  // Actualizar estado del presupuesto
  const handleUpdateEstado = async (presupuestoId, nuevoEstado) => {
    try {
      const response = await fetch(`${API}/presupuestos/${presupuestoId}/estado`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ estado: nuevoEstado })
      });

      if (!response.ok) throw new Error('Error al actualizar estado');

      await loadPresupuestos();
      toast.success(`Estado actualizado a: ${ESTADO_CONFIG[nuevoEstado]?.label || nuevoEstado}`);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al actualizar el estado');
    }
  };

  // Copiar enlace del portal al portapapeles
  const handleCopyPortalLink = (presupuestoId) => {
    const portalUrl = `${window.location.origin}/api/presupuestos/portal/${presupuestoId}`;
    navigator.clipboard.writeText(portalUrl);
    toast.success('Enlace copiado al portapapeles', {
      description: 'Compártelo con tu cliente por WhatsApp o email'
    });
  };

  // Convertir presupuesto en evento
  const handleConvertToEvent = async () => {
    if (!convertingPresupuesto) return;

    setIsConverting(true);
    try {
      const response = await fetch(`${API}/presupuestos/${convertingPresupuesto.id}/convert-to-event`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          tipo_evento: 'Boda'  // Default, could be configurable
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al convertir');
      }

      const result = await response.json();
      
      toast.success('🎉 Evento creado exitosamente', {
        description: `${result.message}. Código de acceso: ${result.access_code}`
      });

      setShowConvertDialog(false);
      setConvertingPresupuesto(null);
      await loadPresupuestos();

      // Notify parent to refresh events list
      if (onEventCreated) {
        onEventCreated(result.evento_id);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.message || 'Error al convertir el presupuesto');
    } finally {
      setIsConverting(false);
    }
  };

  const totals = calculateTotals();

  if (loading) {
    return <div className="text-center py-8">Cargando presupuestos...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-bold text-white flex items-center gap-2">
          <FileText className="w-5 h-5" />
          Presupuestos
        </h3>
        <Button
          onClick={() => setShowForm(!showForm)}
          className="bg-sky-600 hover:bg-sky-700"
        >
          {showForm ? <X className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
          {showForm ? 'Cancelar' : 'Nuevo Presupuesto'}
        </Button>
      </div>

      {showForm && (
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white">
              {editingId ? 'Editar Presupuesto' : 'Crear Presupuesto'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Client Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Cliente</Label>
                  <Input
                    value={formData.cliente_nombre}
                    onChange={(e) => setFormData({ ...formData, cliente_nombre: e.target.value })}
                    className="bg-slate-700 border-slate-600 text-white"
                    required
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Email</Label>
                  <Input
                    type="email"
                    value={formData.cliente_email}
                    onChange={(e) => setFormData({ ...formData, cliente_email: e.target.value })}
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Teléfono</Label>
                  <Input
                    value={formData.cliente_telefono}
                    onChange={(e) => setFormData({ ...formData, cliente_telefono: e.target.value })}
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Fecha Evento</Label>
                  <Input
                    type="date"
                    value={formData.fecha_evento}
                    onChange={(e) => setFormData({ ...formData, fecha_evento: e.target.value })}
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
              </div>

              {/* Services Section */}
              <div className="border-t border-slate-600 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-lg font-semibold text-white">Servicios</h4>
                  <div className="flex gap-2">
                    {playlist?.lugar_evento && (
                      <Badge className="bg-purple-900/50 text-purple-300 flex items-center gap-1">
                        🏢 {playlist.lugar_evento}
                      </Badge>
                    )}
                    <Button
                      type="button"
                      onClick={autoFillServicios}
                      className="bg-purple-600 hover:bg-purple-700"
                      size="sm"
                    >
                      ✨ Auto-rellenar desde Evento
                    </Button>
                  </div>
                </div>
                
                {/* Quick Add from Available Services */}
                {preciosFiltrados.length > 0 && (
                  <div className="bg-slate-700/30 p-3 rounded-lg mb-4">
                    <Label className="text-slate-300 text-sm mb-2 block">
                      📋 Servicios disponibles {playlist?.lugar_evento && `para ${playlist.lugar_evento}`}
                    </Label>
                    <div className="flex flex-wrap gap-2">
                      {preciosFiltrados.map((precio) => (
                        <Button
                          key={precio.id}
                          type="button"
                          size="sm"
                          variant="outline"
                          className={`border-slate-500 text-slate-300 hover:bg-slate-600 text-xs ${
                            precio.venue_exclusivo ? 'border-purple-500 bg-purple-900/30' : ''
                          }`}
                          onClick={() => {
                            setCurrentServicio({
                              nombre: precio.nombre_servicio,
                              descripcion: precio.descripcion || '',
                              precio: precio.precio_base,
                              cantidad: 1
                            });
                          }}
                        >
                          {precio.nombre_servicio} - {precio.precio_base}€
                          {precio.venue_exclusivo && ' 🏢'}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Add Service Form */}
                <div className="bg-slate-700/50 p-4 rounded-lg mb-4 space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div>
                      <Label className="text-slate-300 text-sm">Servicio</Label>
                      <Input
                        value={currentServicio.nombre}
                        onChange={(e) => setCurrentServicio({ ...currentServicio, nombre: e.target.value })}
                        className="bg-slate-700 border-slate-600 text-white"
                        placeholder="Ej: DJ 6 horas"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300 text-sm">Descripción</Label>
                      <Input
                        value={currentServicio.descripcion}
                        onChange={(e) => setCurrentServicio({ ...currentServicio, descripcion: e.target.value })}
                        className="bg-slate-700 border-slate-600 text-white"
                        placeholder="Opcional"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300 text-sm">Precio (€)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={currentServicio.precio}
                        onChange={(e) => setCurrentServicio({ ...currentServicio, precio: parseFloat(e.target.value) || 0 })}
                        className="bg-slate-700 border-slate-600 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300 text-sm">Cantidad</Label>
                      <Input
                        type="number"
                        min="1"
                        value={currentServicio.cantidad}
                        onChange={(e) => setCurrentServicio({ ...currentServicio, cantidad: parseInt(e.target.value) || 1 })}
                        className="bg-slate-700 border-slate-600 text-white"
                      />
                    </div>
                  </div>
                  <Button
                    type="button"
                    onClick={handleAddServicio}
                    className="w-full bg-green-600 hover:bg-green-700"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Añadir Servicio
                  </Button>
                </div>

                {/* Services List - Editable */}
                {formData.servicios.length > 0 && (
                  <div className="space-y-2">
                    {formData.servicios.map((servicio, index) => (
                      <div
                        key={index}
                        className="bg-slate-700/50 p-3 rounded space-y-2"
                      >
                        <div className="flex items-center justify-between">
                          <Input
                            value={servicio.nombre}
                            onChange={(e) => handleUpdateServicio(index, 'nombre', e.target.value)}
                            className="bg-slate-600 border-slate-500 text-white font-semibold flex-1 mr-2"
                          />
                          <Button
                            type="button"
                            onClick={() => handleRemoveServicio(index)}
                            variant="ghost"
                            className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        <Input
                          value={servicio.descripcion}
                          onChange={(e) => handleUpdateServicio(index, 'descripcion', e.target.value)}
                          className="bg-slate-600 border-slate-500 text-slate-300 text-sm"
                          placeholder="Descripción / notas del servicio"
                        />
                        <div className="grid grid-cols-3 gap-2 items-center">
                          <div>
                            <Label className="text-slate-400 text-xs">Precio (€)</Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={servicio.precio}
                              onChange={(e) => handleUpdateServicio(index, 'precio', parseFloat(e.target.value) || 0)}
                              className="bg-slate-600 border-slate-500 text-white"
                            />
                          </div>
                          <div>
                            <Label className="text-slate-400 text-xs">Cantidad</Label>
                            <Input
                              type="number"
                              min="1"
                              value={servicio.cantidad}
                              onChange={(e) => handleUpdateServicio(index, 'cantidad', parseInt(e.target.value) || 1)}
                              className="bg-slate-600 border-slate-500 text-white"
                            />
                          </div>
                          <div>
                            <Label className="text-slate-400 text-xs">Subtotal</Label>
                            <div className="bg-slate-800 border border-slate-600 rounded-md px-3 py-2 text-green-400 font-semibold">
                              {(servicio.precio * servicio.cantidad).toFixed(2)}€
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Totals */}
                    <div className="bg-slate-800 p-4 rounded-lg mt-4 space-y-2">
                      <div className="flex justify-between text-slate-300">
                        <span>Subtotal:</span>
                        <span>{totals.subtotal.toFixed(2)}€</span>
                      </div>
                      {formData.descuento_porcentaje > 0 && (
                        <div className="flex justify-between text-yellow-300">
                          <span>Descuento ({formData.descuento_porcentaje}%):</span>
                          <span>-{totals.total_descuento.toFixed(2)}€</span>
                        </div>
                      )}
                      <div className="flex justify-between text-slate-300 font-semibold">
                        <span>Base Imponible:</span>
                        <span>{totals.base_imponible.toFixed(2)}€</span>
                      </div>
                      {formData.retencion_irpf > 0 && (
                        <div className="flex justify-between text-red-300">
                          <span>Retención IRPF ({formData.retencion_irpf}%):</span>
                          <span>-{totals.total_retencion.toFixed(2)}€</span>
                        </div>
                      )}
                      <div className="flex justify-between text-slate-300">
                        <span>IVA ({formData.iva}%):</span>
                        <span>+{totals.total_iva.toFixed(2)}€</span>
                      </div>
                      <div className="flex justify-between text-xl font-bold text-sky-400 border-t border-slate-600 pt-2">
                        <span>TOTAL:</span>
                        <span>{totals.total.toFixed(2)}€</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Additional Options */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 border-t border-slate-600 pt-4">
                <div>
                  <Label className="text-slate-300">Descuento (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={formData.descuento_porcentaje}
                    onChange={(e) => setFormData({ ...formData, descuento_porcentaje: parseFloat(e.target.value) || 0 })}
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Retención IRPF (%)</Label>
                  <select
                    value={formData.retencion_irpf}
                    onChange={(e) => setFormData({ ...formData, retencion_irpf: parseFloat(e.target.value) })}
                    className="w-full bg-slate-700 border-slate-600 text-white rounded-md p-2"
                  >
                    <option value="0">0% - Sin retención</option>
                    <option value="15">15% - Retención IRPF</option>
                  </select>
                </div>
                <div>
                  <Label className="text-slate-300">IVA (%)</Label>
                  <select
                    value={formData.iva}
                    onChange={(e) => setFormData({ ...formData, iva: parseFloat(e.target.value) })}
                    className="w-full bg-slate-700 border-slate-600 text-white rounded-md p-2"
                  >
                    <option value="0">0% - Exento</option>
                    <option value="10">10% - IVA Reducido</option>
                    <option value="21">21% - IVA General</option>
                  </select>
                </div>
                <div>
                  <Label className="text-slate-300">Validez (días)</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.validez_dias}
                    onChange={(e) => setFormData({ ...formData, validez_dias: parseInt(e.target.value) || 15 })}
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
              </div>

              <div>
                <Label className="text-slate-300">Notas</Label>
                <Textarea
                  value={formData.notas}
                  onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
                  className="bg-slate-700 border-slate-600 text-white"
                  rows={3}
                  placeholder="Condiciones, términos especiales..."
                />
              </div>

              <div className="flex gap-2">
                <Button
                  type="submit"
                  className="flex-1 bg-sky-600 hover:bg-sky-700"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {editingId ? 'Actualizar' : 'Crear'} Presupuesto
                </Button>
                <Button
                  type="button"
                  onClick={resetForm}
                  variant="outline"
                  className="border-slate-600 text-slate-300 hover:bg-slate-700"
                >
                  Cancelar
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Presupuestos List */}
      <div className="space-y-3">
        {presupuestos.length === 0 ? (
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="py-8 text-center text-slate-400">
              No hay presupuestos para este evento
            </CardContent>
          </Card>
        ) : (
          presupuestos.map((presupuesto) => {
            const estadoConfig = ESTADO_CONFIG[presupuesto.estado] || ESTADO_CONFIG.pendiente;
            const canConvert = presupuesto.estado === 'aceptado' && !presupuesto.evento_id;
            
            return (
              <Card key={presupuesto.id} className="bg-slate-800 border-slate-700">
                <CardContent className="p-4">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <h4 className="text-lg font-semibold text-white">
                          {presupuesto.cliente_nombre}
                        </h4>
                        <Badge className={`${estadoConfig.color} flex items-center gap-1`}>
                          <span>{estadoConfig.icon}</span>
                          {estadoConfig.label}
                        </Badge>
                        {presupuesto.evento_id && (
                          <Badge className="bg-green-900/50 text-green-300">
                            ✅ Evento Creado
                          </Badge>
                        )}
                      </div>
                      
                      <div className="text-sm text-slate-400 space-y-1">
                        {presupuesto.cliente_email && (
                          <div>📧 {presupuesto.cliente_email}</div>
                        )}
                        {presupuesto.cliente_telefono && (
                          <div>📱 {presupuesto.cliente_telefono}</div>
                        )}
                        {presupuesto.fecha_evento && (
                          <div>📅 {presupuesto.fecha_evento}</div>
                        )}
                        <div className="font-semibold text-sky-400 mt-2">
                          Total: {presupuesto.total?.toFixed(2)}€ ({presupuesto.servicios?.length || 0} servicios)
                        </div>
                        
                        {/* Tracking info */}
                        <div className="text-xs text-slate-500 mt-2 space-y-0.5">
                          <div>Creado: {new Date(presupuesto.created_at).toLocaleDateString('es-ES')}</div>
                          {presupuesto.fecha_enviado && (
                            <div>📤 Enviado: {new Date(presupuesto.fecha_enviado).toLocaleDateString('es-ES')}</div>
                          )}
                          {presupuesto.fecha_visto && (
                            <div>👁️ Visto: {new Date(presupuesto.fecha_visto).toLocaleDateString('es-ES')}</div>
                          )}
                          {presupuesto.fecha_aceptado && (
                            <div>✅ Aceptado: {new Date(presupuesto.fecha_aceptado).toLocaleDateString('es-ES')} por {presupuesto.aceptado_por}</div>
                          )}
                          {presupuesto.fecha_rechazado && (
                            <div>❌ Rechazado: {new Date(presupuesto.fecha_rechazado).toLocaleDateString('es-ES')}</div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex flex-col gap-2">
                      {/* Primary actions */}
                      <div className="flex gap-2 flex-wrap">
                        <Button
                          onClick={() => handleDownloadPDF(presupuesto.id, presupuesto.cliente_nombre)}
                          className="bg-sky-600 hover:bg-sky-700"
                          size="sm"
                        >
                          <Download className="w-4 h-4 mr-1" />
                          PDF
                        </Button>
                        <Button
                          onClick={() => handleCopyPortalLink(presupuesto.id)}
                          className="bg-purple-600 hover:bg-purple-700"
                          size="sm"
                          title="Copiar enlace para el cliente"
                        >
                          <Link className="w-4 h-4 mr-1" />
                          Enlace
                        </Button>
                      </div>
                      
                      {/* Estado actions */}
                      <div className="flex gap-1 flex-wrap">
                        {presupuesto.estado === 'borrador' && (
                          <Button
                            onClick={() => handleUpdateEstado(presupuesto.id, 'enviado')}
                            className="bg-blue-600 hover:bg-blue-700"
                            size="sm"
                          >
                            <Send className="w-4 h-4 mr-1" />
                            Marcar Enviado
                          </Button>
                        )}
                        {presupuesto.estado === 'enviado' && (
                          <Button
                            onClick={() => handleUpdateEstado(presupuesto.id, 'visto')}
                            className="bg-purple-600 hover:bg-purple-700"
                            size="sm"
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Marcar Visto
                          </Button>
                        )}
                        {(presupuesto.estado === 'visto' || presupuesto.estado === 'enviado') && (
                          <>
                            <Button
                              onClick={() => handleUpdateEstado(presupuesto.id, 'aceptado')}
                              className="bg-green-600 hover:bg-green-700"
                              size="sm"
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Aceptar
                            </Button>
                            <Button
                              onClick={() => handleUpdateEstado(presupuesto.id, 'rechazado')}
                              className="bg-red-600 hover:bg-red-700"
                              size="sm"
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              Rechazar
                            </Button>
                          </>
                        )}
                        {canConvert && (
                          <Button
                            onClick={() => {
                              setConvertingPresupuesto(presupuesto);
                              setShowConvertDialog(true);
                            }}
                            className="bg-emerald-600 hover:bg-emerald-700"
                            size="sm"
                          >
                            <ArrowRight className="w-4 h-4 mr-1" />
                            Crear Evento
                          </Button>
                        )}
                      </div>
                      
                      {/* Edit/Delete */}
                      <div className="flex gap-1">
                        <Button
                          onClick={() => handleEdit(presupuesto)}
                          className="bg-slate-600 hover:bg-slate-500"
                          size="sm"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          onClick={() => handleDelete(presupuesto.id)}
                          className="bg-red-900/50 hover:bg-red-800"
                          size="sm"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Convert to Event Dialog */}
      <Dialog open={showConvertDialog} onOpenChange={setShowConvertDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-xl text-white flex items-center gap-2">
              <ArrowRight className="w-5 h-5 text-emerald-400" />
              Convertir Presupuesto en Evento
            </DialogTitle>
          </DialogHeader>
          
          {convertingPresupuesto && (
            <div className="space-y-4">
              <p className="text-slate-300">
                ¿Deseas crear un evento a partir de este presupuesto?
              </p>
              
              <div className="bg-slate-700/50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-400">Cliente:</span>
                  <span className="text-white font-medium">{convertingPresupuesto.cliente_nombre}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Fecha:</span>
                  <span className="text-white">{convertingPresupuesto.fecha_evento}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Total:</span>
                  <span className="text-emerald-400 font-semibold">{convertingPresupuesto.total?.toFixed(2)}€</span>
                </div>
              </div>
              
              <p className="text-sm text-slate-400">
                Se creará un nuevo evento con los datos del cliente y el presupuesto quedará vinculado.
              </p>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowConvertDialog(false)}
              className="bg-slate-700 border-slate-600 hover:bg-slate-600"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConvertToEvent}
              disabled={isConverting}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {isConverting ? 'Creando...' : '🎉 Crear Evento'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GestionPresupuestos;
